import {
  HStack,
  Text,
  VStack,
  Button,
  Box,
  useColorModeValue,
} from '@chakra-ui/react';
import { ArrowForwardIcon } from '@chakra-ui/icons';
import { ReactNode } from 'react';
import { i18n } from '@investigator/shared/localization';
import { Detection } from '@investigator/shared/generated/graphql';

const { popoverText } = i18n.pages.entities;

/**
 * Props for the DetectionListSection component.
 * @template T - The type of detection objects in the list
 */
interface DetectionListSectionProps {
  /** The title displayed at the top of the section */
  title: string;
  /** The total count of detections to display next to the title */
  count: number;
  /** Array of detection objects to render */
  detections: Detection[];
  /** Optional color token for the circle indicator next to the title */
  circleColor?: string;
  /** Optional text for the "View All" button at the bottom */
  buttonText?: string;
  /** Optional callback fired when the "View All" button is clicked */
  onViewAllClick?: (e: React.MouseEvent) => void;
  /** Whether to show the "View All" button. Defaults to true */
  showButton?: boolean;
  /** Render prop function that renders each detection item */
  children: (detection: Detection, index: number) => ReactNode;
  /** Optional test ID for the section container */
  'data-testid'?: string;
}

/**
 * A reusable section component for displaying a list of detections with an optional header,
 * color indicator, and "View All" button.
 *
 * @template T - The type of detection objects in the list
 *
 * @example
 * ```tsx
 * <DetectionListSection
 *   title="Initial Access"
 *   count={5}
 *   detections={myDetections}
 *   circleColor="red.500"
 *   buttonText="View All Detections"
 *   onViewAllClick={() => navigate('/detections')}
 * >
 *   {(detection, index) => <DetectionRow detection={detection} key={index} />}
 * </DetectionListSection>
 * ```
 */
export const DetectionListSection = ({
  title,
  count,
  detections,
  buttonText,
  onViewAllClick,
  circleColor,
  showButton = true,
  children,
  'data-testid': dataTestId,
}: DetectionListSectionProps): JSX.Element => {
  const displayWord =
    count === 1 ? popoverText.detection : popoverText.detections;

  const buttonBg = useColorModeValue('gray.100', 'gray.700');
  const buttonColor = useColorModeValue('gray.800', 'gray.100');

  return (
    <Box paddingY={2} data-testid={dataTestId}>
      <HStack justifyContent="space-between">
        <HStack spacing={2}>
          {circleColor && (
            <Box w="8px" h="8px" borderRadius="full" bg={circleColor} />
          )}
          <Text fontWeight="semibold" color="text.primary">
            {title}
          </Text>
        </HStack>
        <Text color="text.primary">{`${count} ${displayWord}`}</Text>
      </HStack>
      <VStack
        spacing={2}
        align="stretch"
        borderRadius="md"
        overflow="hidden"
        mt={3}
      >
        <VStack spacing={0} align="stretch" borderRadius="md" overflow="hidden">
          {detections.map((detection: Detection, index: number) => (
            <Box key={detection.detection_id || index}>
              {children(detection, index)}
            </Box>
          ))}
        </VStack>
        {showButton && buttonText && onViewAllClick && (
          <Button
            variant="solid"
            width="full"
            bg={buttonBg}
            color={buttonColor}
            rightIcon={<ArrowForwardIcon />}
            onClick={onViewAllClick}
          >
            {buttonText}
          </Button>
        )}
      </VStack>
    </Box>
  );
};
